import useParseURL from 'hooks/useParseURL';
import {
  Box,
  Drawer,
  Divider,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  List,
  ListItem,
  ListSubheader,
  Typography,
  Stack,
  Badge,
} from '@mui/material';

import {PlanIcon, ActionListIcon, FishGroupIcon, FishIcon, OrganizationIcon} from 'styles/icons';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GrainIcon from '@mui/icons-material/Grain';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PivotTableChartIcon from '@mui/icons-material/PivotTableChart';
import TuneIcon from '@mui/icons-material/Tune';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

import SyncAltIcon from '@mui/icons-material/SyncAlt';
import FactoryIcon from '@mui/icons-material/Factory';
import DashboardIcon from '@mui/icons-material/Dashboard';
import LabelIcon from '@mui/icons-material/LabelOutlined';

import {URLPathname} from 'interfaces';

import {useTranslation} from 'react-i18next';
import BackupTableIcon from '@mui/icons-material/BackupTable';

import {useCagesWithUnallocatedBiomass, useGetCompanies, useGetPlan} from 'hooks/swr';
import useLanguage from 'hooks/useLanguage';
import PlanSyncronizeButton from 'components/PlanSyncronizeButton';
import {LEFT_DRAWER_WIDTH, APP_TOP_BAR_HEIGHT, ICON_SIZE} from 'appConstants';
import {PlanActionsMenu} from './LeftMenu/PlanActionsMenu';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useGetIsChilePlan} from 'hooks/useGetIsChilePlan';

export function LeftDrawerPermanent() {
  const {pathname, planId, navigate, search, state} = useParseURL();
  const {fishGroupPropertiesEnabled, ownership: ownershipEnabled} = useFlags();
  const {isChilePlan} = useGetIsChilePlan();
  const {data: companies} = useGetCompanies({resourceId: planId});
  const displayLiveFishTransactions = ownershipEnabled && Boolean(companies && companies.length > 1);

  const {data: unallocatedBiomass} = useCagesWithUnallocatedBiomass({resourceId: planId});
  const _unallocatedBiomass = unallocatedBiomass && unallocatedBiomass?.length > 0;

  const {data: plan} = useGetPlan({resourceId: planId});
  const {dateFormatter} = useLanguage();

  const {t} = useTranslation('common');

  const planning = [
    {
      icon: <DashboardIcon fontSize={ICON_SIZE} />,
      title: t('overview'),
      pathname: URLPathname.planOverview,
      testid: 'left-drawer-overview',
      enabled: true,
    },
    {
      icon: <BackupTableIcon fontSize={ICON_SIZE} />,
      title: t('overviewTable'),
      pathname: URLPathname.overviewTable,
      testid: 'left-drawer-overview-table',
      enabled: true,
    },
    {
      icon: <ViewWeekIcon fontSize={ICON_SIZE} />,
      title: t('shortTermPlanning'),
      pathname: URLPathname.shortTermPlanning,
      testid: 'left-drawer-short-term-planning',
      enabled: true,
    },
    {
      icon: <PlanIcon fontSize={ICON_SIZE} />,
      title: t('longTermPlanning'),
      pathname: URLPathname.planning,
      testid: 'left-drawer-plans',
      enabled: true,
    },
    {
      icon: <ActionListIcon fontSize={ICON_SIZE} />,
      title: t('actions'),
      pathname: URLPathname.events,
      testid: 'left-drawer-events',
      enabled: true,
    },
    {
      icon: <FishGroupIcon fontSize={ICON_SIZE} />,
      title: t('fishGroups'),
      pathname: URLPathname.fishGroups,
      testid: 'left-drawer-fish-groups',
      enabled: true,
    },
    {
      icon: <PivotTableChartIcon fontSize={ICON_SIZE} />,
      title: t('unitAnalyzer'),
      pathname: URLPathname.pivotTable,
      testid: 'left-drawer-unit-analizer',
      enabled: true,
    },
    {
      icon: <OrganizationIcon fontSize={ICON_SIZE} />,
      title: t('sitesOverview'),
      pathname: URLPathname.organizationOverview,
      testid: 'left-drawer-sites-overview',
      enabled: true,
    },
  ];

  const configuration = [
    {
      icon: <TrendingUpIcon fontSize={ICON_SIZE} />,
      title: t('planModels'),
      pathname: URLPathname.planModels,
      testid: 'left-drawer-models',
      enabled: true,
    },
    {
      icon: <FishIcon fontSize={ICON_SIZE} />,
      title: t('fishTypes'),
      pathname: URLPathname.fishTypes,
      testid: 'left-drawer-fish-types',
      enabled: true,
    },
    {
      icon: <GrainIcon fontSize={ICON_SIZE} />,
      title: t('smoltManagement'),
      pathname: URLPathname.smoltSupply,
      testid: 'left-drawer-smolt-supply',
      enabled: true,
    },
    {
      icon: (
        <Badge variant="dot" color="error" invisible={!_unallocatedBiomass}>
          <AccountBalanceIcon fontSize={ICON_SIZE} />
        </Badge>
      ),

      title: t('concessions'),
      pathname: URLPathname.concessions,
      testid: 'left-drawer-concessions',
      enabled: !isChilePlan,
    },
    {
      icon: <SyncAltIcon fontSize={ICON_SIZE} />,
      title: t('liveFishTransactions'),
      pathname: URLPathname.liveFishTransactions,
      testid: 'left-drawer-live-fish-transactions',
      enabled: displayLiveFishTransactions && !isChilePlan,
    },
    {
      icon: <CenterFocusStrongIcon fontSize={ICON_SIZE} />,
      title: t('harvestDemand'),
      pathname: URLPathname.planHarvestDemand,
      testid: 'left-drawer-plan-harvest-demand',
      enabled: true,
    },
    {
      icon: <FactoryIcon fontSize={ICON_SIZE} />,
      title: t('harvestPlants'),
      pathname: URLPathname.harvestPlant,
      testid: 'left-drawer-harvest-plant',
      enabled: true,
    },
    {
      icon: <LabelIcon fontSize={ICON_SIZE} />,
      title: t('fishGroupProperties'),
      pathname: URLPathname.fishGroupProperties,
      testid: 'left-drawer-fish-group-properties',
      enabled: fishGroupPropertiesEnabled,
    },
    {
      icon: <TuneIcon fontSize={ICON_SIZE} />,
      title: t('optimizationSettings'),
      pathname: URLPathname.optimizationSettings,
      testid: 'left-drawer-optimize',
      enabled: true,
    },
  ];

  if (!planId) {
    return <div></div>;
  } else {
    return (
      <Drawer
        sx={{
          width: LEFT_DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {width: LEFT_DRAWER_WIDTH, boxSizing: 'border-box'},
        }}
        variant="permanent"
        anchor="left"
      >
        <Box height={APP_TOP_BAR_HEIGHT} />
        <Box id="onboarding-main-left-drawer" display="flex" height="100%" flexDirection="column" overflow="hidden">
          <Stack direction="column" p={2} flexGrow={0} spacing={1} sx={{wordBreak: 'break-word'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{plan?.name}</Typography>
              {plan && <PlanActionsMenu plan={plan} />}
            </Stack>
            {plan && (
              <Typography variant="caption" color="text.disabled">
                {dateFormatter.toLongDateString(plan.startDate)} - {dateFormatter.toLongDateString(plan.endDate)}
              </Typography>
            )}
            <PlanSyncronizeButton />
          </Stack>
          <Divider variant="middle" />
          <Box flexGrow={1} sx={{overflowY: 'auto'}} width={LEFT_DRAWER_WIDTH}>
            <List subheader={<ListSubheader>{t('planning')}</ListSubheader>} sx={{overflow: 'auto'}} dense>
              {planning
                .filter((m) => m.enabled)
                .map((menuItem) => {
                  return (
                    <ListItem disablePadding key={menuItem.pathname}>
                      <ListItemButton
                        data-testid={menuItem.testid}
                        selected={pathname.startsWith(menuItem.pathname)}
                        onClick={() => {
                          const from = state && state.from ? state.from.concat(menuItem.pathname) : [menuItem.pathname];
                          navigate(
                            {
                              pathname: menuItem.pathname,
                              search: search.toString(),
                            },
                            {state: {from}},
                          );
                        }}
                      >
                        <ListItemIcon sx={{minWidth: '36px'}}>{menuItem.icon}</ListItemIcon>
                        <ListItemText primary={menuItem.title} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>
            <List subheader={<ListSubheader>{t('configuration')}</ListSubheader>} sx={{overflow: 'auto'}} dense>
              {configuration
                .filter((m) => m.enabled)
                .map((menuItem) => {
                  return (
                    <ListItem disablePadding key={menuItem.pathname}>
                      <ListItemButton
                        data-testid={menuItem.testid}
                        selected={pathname.startsWith(menuItem.pathname)}
                        onClick={() => {
                          const from = state && state.from ? state.from.concat(menuItem.pathname) : [menuItem.pathname];
                          navigate(
                            {
                              pathname: menuItem.pathname,
                              search: search.toString(),
                            },
                            {state: {from}},
                          );
                        }}
                      >
                        <ListItemIcon sx={{minWidth: '36px'}}>{menuItem.icon}</ListItemIcon>
                        <ListItemText primary={menuItem.title} />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
            </List>
          </Box>
        </Box>
      </Drawer>
    );
  }
}
